import React, { useState, useEffect, useCallback } from 'react';
import { Lightbulb, X, ChevronRight, Sparkles } from 'lucide-react';
import { Button, Card } from '../ui';
import type { MorningPageInsightDto } from '../../services';
import './AhaMomentNotification.css';

interface AhaMomentNotificationProps {
  insight: MorningPageInsightDto;
  onDismiss?: () => void;
  onClick?: (insight: MorningPageInsightDto) => void;
  autoHideAfter?: number; // milliseconds, 0 to disable
  position?: 'top-right' | 'bottom-right' | 'bottom-center';
}

export const AhaMomentNotification: React.FC<AhaMomentNotificationProps> = ({
  insight,
  onDismiss,
  onClick,
  autoHideAfter = 10000,
  position = 'bottom-right',
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);

  const handleDismiss = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      setIsVisible(false);
      onDismiss?.();
    }, 300);
  }, [onDismiss]);

  const handleClick = useCallback(() => {
    onClick?.(insight);
    handleDismiss();
  }, [insight, onClick, handleDismiss]);

  useEffect(() => {
    if (autoHideAfter > 0) {
      const timer = setTimeout(handleDismiss, autoHideAfter);
      return () => clearTimeout(timer);
    }
  }, [autoHideAfter, handleDismiss]);

  if (!isVisible) return null;

  const insightTypeEmoji = {
    Theme: '🎨',
    Emotion: '💭',
    Pattern: '📈',
    SelfSabotage: '⚡',
    Recommendation: '💡',
  };

  return (
    <div
      className={`aha-notification aha-notification--${position} ${isExiting ? 'aha-notification--exiting' : ''}`}
      role="alert"
      aria-live="polite"
    >
      <Card variant="elevated" padding="none" className="aha-notification__card">
        <div className="aha-notification__glow" />

        <button
          className="aha-notification__close"
          onClick={handleDismiss}
          aria-label="Dismiss notification"
        >
          <X size={16} />
        </button>

        <div className="aha-notification__content" onClick={handleClick}>
          <div className="aha-notification__header">
            <div className="aha-notification__icon">
              <Lightbulb size={24} />
            </div>
            <div className="aha-notification__badge">
              <Sparkles size={12} />
              Aha Moment!
            </div>
          </div>

          <div className="aha-notification__body">
            <span className="aha-notification__emoji">
              {insightTypeEmoji[insight.insightType] || '✨'}
            </span>
            <h4 className="aha-notification__title">{insight.title}</h4>
            <p className="aha-notification__text">{insight.content}</p>
          </div>

          <div className="aha-notification__footer">
            <span className="aha-notification__type">{insight.insightType}</span>
            <Button variant="ghost" size="sm" className="aha-notification__action">
              View Details
              <ChevronRight size={16} />
            </Button>
          </div>
        </div>

        <div className="aha-notification__progress">
          <div
            className="aha-notification__progress-bar"
            style={{
              animationDuration: `${autoHideAfter}ms`,
              animationPlayState: autoHideAfter > 0 ? 'running' : 'paused',
            }}
          />
        </div>
      </Card>
    </div>
  );
};

// Notification Manager Hook
interface UseAhaMomentOptions {
  maxNotifications?: number;
  defaultAutoHide?: number;
}

export const useAhaMoment = (options: UseAhaMomentOptions = {}) => {
  const { maxNotifications = 3, defaultAutoHide = 10000 } = options;
  const [notifications, setNotifications] = useState<MorningPageInsightDto[]>([]);

  const showAhaMoment = useCallback((insight: MorningPageInsightDto) => {
    setNotifications((prev) => {
      // Keep only the most recent notifications up to maxNotifications
      const updated = [insight, ...prev].slice(0, maxNotifications);
      return updated;
    });
  }, [maxNotifications]);

  const dismissAhaMoment = useCallback((insightId: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== insightId));
  }, []);

  const dismissAll = useCallback(() => {
    setNotifications([]);
  }, []);

  return {
    notifications,
    showAhaMoment,
    dismissAhaMoment,
    dismissAll,
    defaultAutoHide,
  };
};

// Container for multiple notifications
interface AhaMomentContainerProps {
  notifications: MorningPageInsightDto[];
  onDismiss: (id: string) => void;
  onClick?: (insight: MorningPageInsightDto) => void;
  autoHideAfter?: number;
  position?: 'top-right' | 'bottom-right' | 'bottom-center';
}

export const AhaMomentContainer: React.FC<AhaMomentContainerProps> = ({
  notifications,
  onDismiss,
  onClick,
  autoHideAfter = 10000,
  position = 'bottom-right',
}) => {
  if (notifications.length === 0) return null;

  return (
    <div className={`aha-notification-container aha-notification-container--${position}`}>
      {notifications.map((insight, index) => (
        <AhaMomentNotification
          key={insight.id}
          insight={insight}
          onDismiss={() => onDismiss(insight.id)}
          onClick={onClick}
          autoHideAfter={autoHideAfter + index * 500} // Stagger auto-hide
          position={position}
        />
      ))}
    </div>
  );
};

export default AhaMomentNotification;
